import { useContext, useEffect, useMemo } from 'react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { TableRowData } from '@Components/ViewWork/Table'
import bulkAmendmentTableCells from '@App/helpers/overclaim/bulk-amendment-table-cells'

export interface AddBulkAmendmentTableInfoProps {
    updateClaimedShares: (key: string, newClaimedShares: number) => void
    setDisplayNextButton: (display: boolean) => void
}

const AddBulkAmendmentTableInfo = (props: AddBulkAmendmentTableInfoProps) => {
    const { updateClaimedShares, setDisplayNextButton } = props
    const { overclaimSearchStore, overclaimSearchBulkActionStore } = useContext<RootStore>(StoresContext)

    if (!overclaimSearchStore.worksRightsHolder || overclaimSearchStore.worksRightsHolder.works.length === 0) {
        return []
    }

    const hasChanges = useMemo(
        () =>
            Array.from(overclaimSearchBulkActionStore.groupedOverclaimsForAmendment.values()).some(
                (group) => group.claimedShares !== group.initialClaimedShares
            ),
        [overclaimSearchBulkActionStore.groupedOverclaimsForAmendment]
    )

    const validShares = useMemo(
        () =>
            Array.from(overclaimSearchBulkActionStore.groupedOverclaimsForAmendment.values()).every(
                (group) => group.claimedShares > 0
            ),
        [overclaimSearchBulkActionStore.groupedOverclaimsForAmendment]
    )

    useEffect(() => {
        setDisplayNextButton(hasChanges && validShares)
    }, [hasChanges, validShares, setDisplayNextButton])

    const toggleShareEditing = (key: string) => {
        const updatedGroupedOverclaims = new Map(overclaimSearchBulkActionStore.groupedOverclaimsForAmendment)
        const groupToToggle = updatedGroupedOverclaims.get(key)
        if (groupToToggle) {
            updatedGroupedOverclaims.set(key, {
                ...groupToToggle,
                allowShareAmendment: !groupToToggle.allowShareAmendment,
            })
            overclaimSearchBulkActionStore.setGroupedOverclaimsForAmendment(updatedGroupedOverclaims)
        }
    }

    const viewGroupDetails = async (overclaimIds: number[]) => {
        overclaimSearchBulkActionStore.setActiveBulkAction(null, true)
        await overclaimSearchStore.secondarySearch({ overclaimHeaderIds: overclaimIds })
    }

    const tableRows: TableRowData[] = useMemo(() => {
        if (overclaimSearchBulkActionStore.groupedOverclaimsForAmendment.size === 0) {
            return []
        }
        return Array.from(overclaimSearchBulkActionStore.groupedOverclaimsForAmendment.values()).map((group) => ({
            removable: false,
            cells: bulkAmendmentTableCells({
                group,
                allowShareEditing: group.allowShareAmendment,
                toggleShareEditing,
                updateClaimedShares,
                viewGroupDetails,
                inReview: overclaimSearchBulkActionStore.isAmendmentInReview,
            }),
        }))
    }, [
        overclaimSearchBulkActionStore.groupedOverclaimsForAmendment,
        overclaimSearchBulkActionStore.isAmendmentInReview,
    ])

    const headers = useMemo(() => {
        const baseHeaders = [{ text: 'Works in Group' }, { text: 'Publisher/Administrator' }, { text: 'Your Share' }]
        if (overclaimSearchBulkActionStore.isAmendmentInReview) {
            baseHeaders.push({ text: '' })
            baseHeaders.push({ text: 'New Share' })
        }
        if (!overclaimSearchBulkActionStore.isAmendmentInReview) {
            baseHeaders.push({ text: 'Actions' })
        }
        return baseHeaders
    }, [overclaimSearchBulkActionStore.isAmendmentInReview])

    return [
        {
            headerRow: {
                row: headers,
                removableCells: false,
            },
            rows: tableRows,
        },
    ]
}
export default AddBulkAmendmentTableInfo
